import { useCallback, useEffect, useState } from "react";
import { Eye, EyeOff, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { http, formatError } from "@/lib/site";
import { auth } from "./auth";

const empty = { nombre: "", comuna: "", texto: "", visible: true };

export const TestimonialsTab = ({ onLogout }) => {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState(empty);
  const [editId, setEditId] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await http.get("/admin/testimonials", auth());
      setItems(Array.isArray(data) ? data : []);
    } catch (err) {
      if (err.response?.status === 401) onLogout();
    }
  }, [onLogout]);
  useEffect(() => { load(); }, [load]);

  const save = async (e) => {
    e.preventDefault();
    try {
      if (editId) await http.put(`/admin/testimonials/${editId}`, form, auth());
      else await http.post("/admin/testimonials", form, auth());
      toast.success(editId ? "Testimonio actualizado" : "Testimonio agregado");
      setForm(empty); setEditId(null); load();
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
  };
  const toggle = async (t) => { await http.put(`/admin/testimonials/${t.id}`, { nombre: t.nombre, comuna: t.comuna, texto: t.texto, visible: !t.visible }, auth()); load(); };
  const remove = async (id) => { if (window.confirm("¿Eliminar este testimonio?")) { await http.delete(`/admin/testimonials/${id}`, auth()); load(); } };
  const edit = (t) => { setEditId(t.id); setForm({ nombre: t.nombre, comuna: t.comuna, texto: t.texto, visible: t.visible }); window.scrollTo({ top: 0, behavior: "smooth" }); };

  return (
    <div className="grid gap-8 lg:grid-cols-[380px_1fr]">
      <form onSubmit={save} className="h-fit space-y-4 rounded-2xl border border-[#E3EAEF] bg-white p-6" data-testid="testimonial-form">
        <h2 className="text-[20px] font-extrabold text-[#0B3C5D]">{editId ? "Editar testimonio" : "Agregar testimonio"}</h2>
        <p className="text-[15px] text-[#4A5864]">Publica solo comentarios reales y con autorización de la persona.</p>
        <input className="field" required placeholder="Nombre (ej: Carmen R.)" value={form.nombre} onChange={(e) => setForm({ ...form, nombre: e.target.value })} data-testid="testimonial-nombre" />
        <input className="field" placeholder="Comuna (opcional)" value={form.comuna} onChange={(e) => setForm({ ...form, comuna: e.target.value })} data-testid="testimonial-comuna" />
        <textarea className="field !min-h-[140px] py-3" required placeholder="Comentario" value={form.texto} onChange={(e) => setForm({ ...form, texto: e.target.value })} data-testid="testimonial-texto" />
        <label className="flex items-center gap-3 text-[16px]"><input type="checkbox" className="h-5 w-5 accent-[#0B3C5D]" checked={form.visible} onChange={(e) => setForm({ ...form, visible: e.target.checked })} data-testid="testimonial-visible" /> Mostrar en la página</label>
        <div className="flex gap-2">
          <button className="min-h-[52px] flex-1 rounded-xl bg-[#0B3C5D] text-[17px] font-bold text-white" data-testid="testimonial-save">{editId ? "Guardar cambios" : "Agregar"}</button>
          {editId && <button type="button" onClick={() => { setEditId(null); setForm(empty); }} className="min-h-[52px] rounded-xl border border-[#E3EAEF] px-4 font-semibold" data-testid="testimonial-cancel">Cancelar</button>}
        </div>
      </form>
      <div className="space-y-3" data-testid="testimonial-list">
        {items.length ? items.map((t) => (
          <div key={t.id} className={`rounded-2xl border border-[#E3EAEF] bg-white p-5 ${t.visible ? "" : "opacity-60"}`} data-testid={`admin-testimonial-${t.id}`}>
            <p className="text-[17px] leading-relaxed text-[#1F2A33]">“{t.texto}”</p>
            <div className="mt-3 flex items-center justify-between gap-3">
              <span className="text-[15px] font-bold text-[#0B3C5D]">{t.nombre}{t.comuna && ` · ${t.comuna}`}{!t.visible && " (oculto)"}</span>
              <div className="flex gap-2">
                <button onClick={() => toggle(t)} aria-label={t.visible ? "Ocultar" : "Mostrar"} className="flex h-10 w-10 items-center justify-center rounded-full border border-[#E3EAEF]" data-testid={`testimonial-toggle-${t.id}`}>{t.visible ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}</button>
                <button onClick={() => edit(t)} aria-label="Editar" className="flex h-10 w-10 items-center justify-center rounded-full border border-[#E3EAEF]" data-testid={`testimonial-edit-${t.id}`}><Pencil className="h-5 w-5" /></button>
                <button onClick={() => remove(t.id)} aria-label="Eliminar" className="flex h-10 w-10 items-center justify-center rounded-full border border-[#E3EAEF] hover:bg-red-50" data-testid={`testimonial-delete-${t.id}`}><Trash2 className="h-5 w-5" /></button>
              </div>
            </div>
          </div>
        )) : <p className="py-16 text-center text-[18px] text-[#4A5864]">Aún no hay testimonios. La sección no se muestra en la página hasta que agregues uno.</p>}
      </div>
    </div>
  );
};
